
import React, { useEffect, useMemo, useState } from 'react';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ModelRow } from '@/components/ModelRow';
import { useToast } from '@/hooks/use-toast';
import { useMarkets } from '@/hooks/useMarkets';
import { useModels } from '@/hooks/useModels';
import { supabase } from '@/integrations/supabase/client';

const ModelOverview = () => {
  const { toast } = useToast();
  const { masterData, adData, atpData, refetchMaster } = useModels();
  const [selectedMarket, setSelectedMarket] = useState<string>("GER");
  const [selectedMaker, setSelectedMaker] = useState<string | undefined>(undefined);
  const [showMissingAtp, setShowMissingAtp] = useState(false);
  const [showMissingAd, setShowMissingAd] = useState(false);

  // Get unique markets from all data sources
  const uniqueMarkets = useMarkets(masterData, adData, atpData);

  // If GER is not available in the markets list, select the first available market
  useEffect(() => {
    if (uniqueMarkets.length > 0 && !uniqueMarkets.includes(selectedMarket)) {
      setSelectedMarket(uniqueMarkets[0]);
    }
  }, [uniqueMarkets, selectedMarket]);

  // Get unique makers from all data sources
  const uniqueMakers = useMemo(() => {
    const makers = new Set<string>();
    [...(masterData || []), ...(adData || []), ...(atpData || [])]
      .forEach(model => {
        if (model.maker_id) {
          makers.add(model.maker_id.toString());
        }
      });
    return Array.from(makers).sort((a, b) => Number(a) - Number(b));
  }, [masterData, adData, atpData]);

  // Select the first maker by default when the page loads
  useEffect(() => {
    if (uniqueMakers.length > 0 && !selectedMaker) {
      setSelectedMaker(uniqueMakers[0]);
    }
  }, [uniqueMakers, selectedMaker]);

  // Filter data by selected market
  const filteredMasterData = useMemo(() => 
    masterData?.filter(m => 
      (!selectedMarket || m.market === selectedMarket) &&
      (!selectedMaker || m.maker_id.toString() === selectedMaker)
    ) || [], 
    [masterData, selectedMarket, selectedMaker]
  );

  const filteredAdData = useMemo(() => 
    adData?.filter(m => 
      (!selectedMarket || m.market === selectedMarket) &&
      (!selectedMaker || m.maker_id.toString() === selectedMaker)
    ) || [], 
    [adData, selectedMarket, selectedMaker]
  );

  const filteredAtpData = useMemo(() => 
    atpData?.filter(m => 
      (!selectedMarket || m.market === selectedMarket) &&
      (!selectedMaker || m.maker_id.toString() === selectedMaker)
    ) || [], 
    [atpData, selectedMarket, selectedMaker]
  );

  // Create a combined list of all unique models
  const allModels = useMemo(() => {
    const modelMap = new Map();

    // Process all data sources
    [...(filteredMasterData || []), ...(filteredAdData || []), ...(filteredAtpData || [])]
      .forEach(model => {
        const key = `${model.model_id}_${model.market}`;
        if (!modelMap.has(key)) {
          const masterObj = filteredMasterData?.find(m => m.model_id === model.model_id);
          const adObj = filteredAdData?.find(m => m.model_id === model.model_id);
          const atpObj = filteredAtpData?.find(m => m.model_id === model.model_id);

          // Apply filters for missing models
          if (showMissingAtp && atpObj) return;
          if (showMissingAd && adObj) return;

          modelMap.set(key, {
            model_id: model.model_id,
            maker_id: model.maker_id,
            name: model.model_name,
            market: model.market,
            masterObj,
            adObj,
            atpObj
          });
        }
      });

    return Array.from(modelMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredMasterData, filteredAdData, filteredAtpData, showMissingAtp, showMissingAd]);

  const handleImport = async (sourceModel: any) => {
    if (!sourceModel || !selectedMarket) return;
    
    const { error } = await supabase
      .from('modeldb_master')
      .insert({
        model_name: sourceModel.model_name,
        model_id: sourceModel.model_id,
        maker_id: sourceModel.maker_id,
        activated: true,
        market: selectedMarket,
        ranking: sourceModel.ranking
      });

    if (error) {
      toast({
        variant: "destructive",
        title: "Import failed",
        description: "Could not import model to master database",
      });
    } else {
      toast({
        title: "Success",
        description: `${sourceModel.model_name} was imported to master database`,
      });
      refetchMaster();
    }
  };

  const handleRemove = async (modelId: number, market: string) => {
    const { error } = await supabase
      .from('modeldb_master')
      .delete()
      .eq('model_id', modelId)
      .eq('market', market);

    if (error) {
      toast({
        variant: "destructive",
        title: "Removal failed",
        description: "Could not remove model from master database"
      });
    } else {
      toast({
        title: "Removed",
        description: `Model removed from master database`,
      });
      refetchMaster();
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Model Overview</h1>
      
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Select value={selectedMarket} onValueChange={setSelectedMarket}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select market" />
            </SelectTrigger>
            <SelectContent>
              {uniqueMarkets.map((market) => (
                <SelectItem key={market} value={market}>{market}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedMaker} onValueChange={setSelectedMaker}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select maker" />
            </SelectTrigger>
            <SelectContent>
              {uniqueMakers.map((maker) => (
                <SelectItem key={maker} value={maker}>Maker {maker}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-8">
          <div className="flex items-center space-x-2">
            <Switch 
              id="missing-atp" 
              checked={showMissingAtp}
              onCheckedChange={setShowMissingAtp}
            />
            <Label htmlFor="missing-atp">Missing at ATP</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch 
              id="missing-ad" 
              checked={showMissingAd}
              onCheckedChange={setShowMissingAd}
            />
            <Label htmlFor="missing-ad">Missing at AD</Label>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mt-4">
        <Table>
          <TableCaption>Vehicle Models Overview</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              <TableHead>Master</TableHead>
              <TableHead>AD</TableHead>
              <TableHead>ATP</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allModels.map((model) => (
              <ModelRow
                key={`${model.model_id}_${model.market}`}
                model={model}
                masterPresent={!!model.masterObj}
                adPresent={!!model.adObj}
                atpPresent={!!model.atpObj}
                onImport={handleImport}
                onRemove={handleRemove}
                importSourceObj={model.adObj || model.atpObj}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ModelOverview;
